import { Badge, Divider, Layout, Typography } from 'antd';
import { CalendarIcon, NotificationIcon } from 'assets';
import { MenuItem } from 'components/common';
import { CustomerSupportInformationList } from 'components/customer-support-information';
import CustomerSupportInformationDetail from 'components/customer-support-information/CustomerSupportInformationDetail';
import { InternalAnnouncementsModal } from 'components/internal-announcements';
import { useSystemDatetime } from 'contexts';
import { useInternalAnnouncementsUnreadCount, useSearchParamsForm } from 'hooks';
import { sidebarMenuMessages } from 'messages';
import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { colorsBase, sizes } from 'themes';
import { CustomerSupportInformationDto } from 'types';
import { DATE_FORMAT, DAY_FORMAT, SIDEBAR_MENU_KEY, TIME_FORMAT } from 'utils';

const { Sider } = Layout;

const SideBarMenuCustomerLookup = () => {
  const { getSearchParams } = useSearchParamsForm();
  const { IA } = getSearchParams();
  const { data: unreadCount } = useInternalAnnouncementsUnreadCount();
  const { datetime } = useSystemDatetime();

  const timeoutItemRef = useRef<number | null>(null);

  const [collapsed, setCollapsed] = useState(true);
  const [openInternalAnnouncement, setOpenInternalAnnouncement] = useState(false);
  const [openCustomerSupportInfo, setOpenCustomerSupportInfo] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<Omit<CustomerSupportInformationDto, 'content'>>();

  useEffect(() => {
    if (IA) {
      setOpenInternalAnnouncement(true);
    }
  }, [IA]);

  const onOpenSider = () => {
    setCollapsed(false);
    if (timeoutItemRef.current) {
      clearTimeout(timeoutItemRef.current);
    }
  };
  const onCloseSider = () => {
    if (timeoutItemRef.current) {
      clearTimeout(timeoutItemRef.current);
    }
    timeoutItemRef.current = window.setTimeout(() => {
      setCollapsed(true);
    }, 400);
  };
  const handleCollapsed = () => {
    setCollapsed(true);
  };

  return (
    <div>
      <div className='sidebar-menu flex' onMouseEnter={onOpenSider} onMouseLeave={onCloseSider}>
        <Sider
          collapsed={collapsed}
          collapsedWidth={56}
          width={sizes.sidebarMenu}
          className={twMerge('transition-all duration-[300ms]', collapsed && 'main-menu-sider-collapsed')}
        >
          <div className='content flex h-full flex-col bg-colorBgSpotlight pb-16 pt-4'>
            <div className={twMerge('flex h-14 items-center', !collapsed && 'px-4')}>
              <Typography.Text
                className={twMerge(
                  'clock-time whitespace-nowrap font-[Orbitron] font-extrabold text-white transition-all',
                  collapsed ? 'm-auto text-sm' : 'text-2xl'
                )}
              >
                {datetime.format(TIME_FORMAT)}
              </Typography.Text>
            </div>
            <Divider prefixCls='customer-lookup-divider' />

            {collapsed ? (
              <div className='flex h-12 flex-col items-center whitespace-nowrap'>
                <Typography.Text ellipsis className='text-0.5xl font-extrabold text-white'>
                  {datetime.format('DD')}
                </Typography.Text>
                <Typography.Text className='text-[10px] font-extrabold text-white'>
                  Tháng {datetime.format('M')}
                </Typography.Text>
              </div>
            ) : (
              <div className='item-option h-12 px-4'>
                <Typography.Text ellipsis className={twMerge('font-semibold')}>
                  {datetime.format(DAY_FORMAT)}, {datetime.format(DATE_FORMAT)}
                </Typography.Text>
                <div className='ml-auto'>
                  <CalendarIcon />
                </div>
              </div>
            )}

            <Divider prefixCls='customer-lookup-divider' />
            <MenuItem
              onClick={() => {
                handleCollapsed();
                setOpenInternalAnnouncement(true);
              }}
              item={{
                key: SIDEBAR_MENU_KEY.INTERNAL_ANNOUNCEMENTS,
                label: (
                  <Typography.Text className='text-white'>
                    {sidebarMenuMessages.internalAnnouncement}
                    {unreadCount ? (
                      <Typography.Text className='ml-2 rounded-lg bg-colorBgSuccess px-2 text-sm text-white'>
                        {unreadCount}
                      </Typography.Text>
                    ) : undefined}
                  </Typography.Text>
                )
              }}
              icon={
                <Badge dot={collapsed && !!unreadCount} color={colorsBase.colorBgSuccess}>
                  <NotificationIcon className='text-white' />
                </Badge>
              }
              className='px-4'
            />
            <Divider prefixCls='customer-lookup-divider' />
            <div className='h-full w-full overflow-y-auto overflow-x-hidden'>
              <CustomerSupportInformationList
                handleOnSelectItem={(itm) => {
                  setOpenCustomerSupportInfo(true);
                  setCustomerInfo(itm);
                }}
                handleCollapsed={handleCollapsed}
              />
            </div>
          </div>
        </Sider>
      </div>
      <InternalAnnouncementsModal
        open={openInternalAnnouncement}
        onCancel={() => {
          setOpenInternalAnnouncement(false);
        }}
      />
      <CustomerSupportInformationDetail
        open={openCustomerSupportInfo}
        onCancel={() => {
          setOpenCustomerSupportInfo(false);
        }}
        customerSupportInformationId={customerInfo?.customerSupportInformationId}
      />
    </div>
  );
};

export default SideBarMenuCustomerLookup;
